import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import type { ApiSuccess, AuthTokens } from '@/lib/shared-types';

export interface TokenStore {
  get(): Promise<AuthTokens | null> | AuthTokens | null;
  set(tokens: AuthTokens | null): Promise<void> | void;
}

/**
 * The two things anything that needs a *current* access token has to know: where
 * `/auth/refresh` lives, and where the tokens are kept.
 *
 * Split out of `ClientOptions` so the realtime token getter can be built from
 * the same object the HTTP client was built from. That is not tidiness — see
 * `refreshAccessTokens` below for why they must share one store instance.
 */
export interface TokenSource {
  baseURL: string;
  tokenStore: TokenStore;
}

export interface ClientOptions extends TokenSource {
  /** Sent as X-Tenant-Id when the caller is not yet authenticated. */
  tenantId?: string;
  locale?: string;
  onUnauthorized?: () => void;
}

/**
 * Structural, not `instanceof FormData` — this client is shared with the native apps, which have
 * their own `FormData` and no DOM lib to type it from.
 */
function isFormData(data: unknown): boolean {
  return (
    typeof data === 'object' &&
    data !== null &&
    typeof (data as { append?: unknown }).append === 'function' &&
    typeof (data as { getAll?: unknown }).getAll === 'function'
  );
}

export function createHttpClient(options: ClientOptions): AxiosInstance {
  const instance = axios.create({
    baseURL: options.baseURL.replace(/\/$/, ''),
    timeout: 20000,
    headers: { 'Content-Type': 'application/json' },
  });

  instance.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    const tokens = await options.tokenStore.get();
    if (tokens?.accessToken) {
      config.headers.set('Authorization', `Bearer ${tokens.accessToken}`);
    }
    if (options.tenantId) config.headers.set('X-Tenant-Id', options.tenantId);
    if (options.locale) config.headers.set('x-lang', options.locale);

    // A file upload is `multipart/form-data` with a per-request boundary. The JSON default above
    // would keep that boundary off the header, so the server sees a body it cannot split and
    // reports no file at all. Deleting the header lets axios set the correct one from the data.
    if (isFormData(config.data)) config.headers.delete('Content-Type');
    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      // Unwrap the { success, data, meta } envelope, keeping meta reachable.
      const body = response.data as ApiSuccess<unknown>;
      if (body && typeof body === 'object' && 'success' in body && body.success) {
        response.data = body.meta ? { data: body.data, meta: body.meta } : body.data;
      }
      return response;
    },
    async (error: AxiosError) => {
      const original = error.config as InternalAxiosRequestConfig & { _retried?: boolean };

      if (error.response?.status === 401 && original && !original._retried) {
        original._retried = true;
        const tokens = await refreshAccessTokens(options);
        if (tokens) {
          original.headers.set('Authorization', `Bearer ${tokens.accessToken}`);
          return instance.request(original);
        }
        options.onUnauthorized?.();
      }

      return Promise.reject(error);
    },
  );

  return instance;
}

/**
 * The refresh currently in flight for a given token store, if any.
 *
 * Keyed on the **store object**, not on the client, because there is now more
 * than one caller: the 401 interceptor above and `createRealtimeTokenGetter` in
 * `realtime.ts`, which runs when the gateway refuses a socket handshake. Those
 * two fire together all the time — an access token expires and the next HTTP
 * request and the next socket reconnect both discover it within a second of each
 * other — and **refresh tokens rotate**, so two concurrent refreshes with the
 * same one means the second is rejected and the user is signed out mid-shift.
 * That is the hazard the interceptor's own single-flight already guarded against
 * between HTTP requests; sharing the map extends the same guarantee across the
 * socket. A `WeakMap` so a store from a torn-down client is not pinned in memory.
 */
const refreshing = new WeakMap<TokenStore, Promise<AuthTokens | null>>();

/**
 * Refresh the stored tokens, joining a refresh already running for this store.
 *
 * Resolves to the new tokens, or `null` when there is nothing to refresh with or
 * the server refused — in which case the store has been cleared and the only
 * cure is a fresh sign-in.
 */
export function refreshAccessTokens(source: TokenSource): Promise<AuthTokens | null> {
  const existing = refreshing.get(source.tokenStore);
  if (existing) return existing;

  const pending = refreshTokens(source).finally(() => {
    refreshing.delete(source.tokenStore);
  });
  refreshing.set(source.tokenStore, pending);
  return pending;
}

async function refreshTokens(options: TokenSource): Promise<AuthTokens | null> {
  const current = await options.tokenStore.get();
  if (!current?.refreshToken) return null;
  try {
    const { data } = await axios.post<{ success: true; data: AuthTokens }>(
      `${options.baseURL.replace(/\/$/, '')}/auth/refresh`,
      { refreshToken: current.refreshToken },
    );
    const tokens = data.data;
    await options.tokenStore.set(tokens);
    return tokens;
  } catch {
    await options.tokenStore.set(null);
    return null;
  }
}

/** In-memory store — fine for web; native apps should back this with SecureStore. */
export function memoryTokenStore(initial: AuthTokens | null = null): TokenStore {
  let tokens = initial;
  return {
    get: () => tokens,
    set: (next) => {
      tokens = next;
    },
  };
}
